import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { DynamicFormControlEvent, DynamicFormModel } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { WINDOW } from 'ngx-window-token';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { dateToString } from '../../helpers/date-to-string.helper';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { LENKER_AUSWAEHLEN_TEMPLATE_FACTORY } from './lenker-auswaehlen.config';
import { LenkerAuswaehlenModel, LenkerVorhanden } from './lenker-auswaehlen.model';

@Component({
  selector:    'gsm-lenker-auswaehlen',
  templateUrl: './lenker-auswaehlen.component.html',
  styleUrls:   ['./lenker-auswaehlen.component.scss'],
  imports:     [
    DynamicMaterialFormComponent
  ],
  standalone:  true
})
export class LenkerAuswaehlenComponent extends CustomDynamicFormControlComponent<LenkerAuswaehlenModel> implements OnInit {
  private formService = inject(CustomDynamicFormService);
  private window = inject<Window>(WINDOW);

  @ViewChild('form', {static: true}) formComponent: DynamicMaterialFormComponent;

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    const json = LENKER_AUSWAEHLEN_TEMPLATE_FACTORY(this.model.lenkerList);

    if (this.model.disabled) {
      setModelDisabled(json, true);
    }

    this.formModel = this.formService.fromJSON(json);
    this.formGroup = this.formService.createFormGroup(this.formModel);

    this._updateFormFromValue();
    this._emitChange();
  }

  protected onDisabledChange(): void {
    setModelDisabled(this.formModel, this.model.disabled);
  }

  onFormChange(event: DynamicFormControlEvent): void {
    const lenkerChanged: boolean = !!event.model && event.model.id === 'lenker';

    if (lenkerChanged) {
      this.formGroup.get('lenker_erfassen')
        .reset();
      this.formGroup.get('lenker_erfassen_allgemein')
        .reset();
    }

    if (lenkerChanged && typeof (event.model as any).value === 'object') {
      this.model.value = {
        ...(event.model as any).value,
        vorhanden: LenkerVorhanden.Vorhanden
      };
      this._updateFormFromValue();
    } else {
      // reset geburtsDatum disabled state
      if (lenkerChanged && (event.model as any).value === 'A') {
        this.formService.findModelById('geburtsDatum', this.formModel).disabled = this.model.disabled;
      }
      this._updateValueFromForm();
    }

    this._emitChange();
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const invalid = formElementsInvalid(this.formGroup, this.formModel);
    return invalid ? {lenkerAuswaehlen: true} : null;
  }

  private _updateFormFromValue() {
    if (!this.model.value) {
      return;
    }

    const matchingKontakt =
      (this.model.value.kundeId && this.model.lenkerList.find(item => item.kundeId === this.model.value.kundeId)) || {};

    const {
      vorname,
      nachname,
      geburtsDatum,
      checklistItemList: [{checklistItemFileList}] = [{} as any],
      fuehrerscheinKlassen,
      vorhanden,
      kundeId
    } = {
      ...this.model.value,
      ...matchingKontakt
    };

    const lenker = kundeId ? this.model.value : !vorhanden ? LenkerVorhanden.Anlegen : LenkerVorhanden.fromString(vorhanden);

    this.formGroup.patchValue({
      lenker,
      lenker_erfassen:           {
        vorname,
        nachname
      },
      lenker_erfassen_allgemein: {
        geburtsDatum,
        fuehrerscheinRueckseite: checklistItemFileList,
        fuehrerscheinKlassen
      }
    });

    // Disable/enable geburtsDatum depending on whether matchingKontakt has geburtsDatum.
    // setTimeout is necessary because otherwise it isn't actually disabled on the initial state - appears to be a bug in the library.
    this.window.setTimeout(() => {
      this.formService.findModelById('geburtsDatum', this.formModel).disabled = this.model.disabled || !!matchingKontakt.geburtsDatum;
    }, 0);
  }

  private _updateValueFromForm() {
    const {
      lenker,
      lenker_erfassen:           {
                                   vorname,
                                   nachname
                                 },
      lenker_erfassen_allgemein: {
                                   geburtsDatum,
                                   fuehrerscheinRueckseite: checklistItemFileList,
                                   fuehrerscheinKlassen
                                 }
    } = this.formGroup.getRawValue();

    // If there is no lenker, vorhanden is not selected. if lenker is a string, use that as vorhanden state.
    // Otherwise, lenker is an object -> lenker is vorhanden.
    const vorhanden = !lenker ? null : typeof lenker === 'string' ? LenkerVorhanden.fromString(lenker) : LenkerVorhanden.Vorhanden;

    const checklistItemList = checklistItemFileList && checklistItemFileList.length ? [{checklistItemFileList}] : null;

    this.model.value = filterEmptyProps({
      ...(typeof lenker === 'string' ? {} : lenker),
      vorname,
      nachname,
      geburtsDatum: dateToString(geburtsDatum),
      checklistItemList,
      fuehrerscheinKlassen,
      vorhanden
    });
  }
}
